import { useState, useMemo, useEffect, useRef, useLayoutEffect, useCallback, forwardRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  MapPin,
  FileCheck,
  X,
  Loader2,
  Calendar,
  User,
  MoreVertical,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import useAccountStatusCheck from "../../hooks/useAccountStatusCheck";
import claimsService from "../../api/ClaimsService";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import ConfirmationModal from "../../components/ConfirmationModal";

const parseClaimParts = (claim) => {
  const raw = claim?.incident;
  const items = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [];
  const byKey = (key) => items.find((item) => item?.key === key) || {};
  const keyedIncident = byKey("incident");
  const purchased = byKey("purchased_policy");
  const customer = byKey("customer");
  const hasKeyedIncident = Object.keys(keyedIncident).length > 0;
  const fallback = !hasKeyedIncident && items[0] && !items[0].key ? items[0] : keyedIncident;

  return {
    incident: hasKeyedIncident ? keyedIncident : fallback,
    purchased,
    customer,
  };
};

const personName = (person = {}) =>
  [person.first_name, person.last_name].filter(Boolean).join(" ").trim() || person.full_name || "";

const policyBoughtFor = (claim, customer = {}) => {
  const forSelf = claim?.for_self !== false;
  const buyer = personName(customer) || "Policyholder";
  const representative = claim?.representative;

  if (forSelf) {
    return {
      forSelf: true,
      name: buyer,
      meta: "Bought for self",
      phone: customer.phone_number,
      email: customer.email,
    };
  }

  return {
    forSelf: false,
    name: representative?.full_name || "Someone else",
    meta: representative?.relationship ? `Bought for · ${representative.relationship}` : "Bought for someone else",
    phone: representative?.phone,
    email: representative?.email,
  };
};

const formatCoverDuration = (coverage) => {
  if (!coverage || typeof coverage !== "object") return null;
  if (coverage.display_value != null && coverage.display_unit) {
    const value = coverage.display_value;
    const unit = String(coverage.display_unit);
    const label = Number(value) === 1 ? unit.replace(/s$/, "") : unit;
    return `${value} ${label}`;
  }
  if (coverage.hours) return `${coverage.hours} hours`;
  return null;
};

const coverSummary = (purchased = {}) => {
  const parts = [];
  if (purchased.vehicleName) parts.push(purchased.vehicleName);
  else if (purchased.vehicleType) parts.push(purchased.vehicleType);
  if (purchased.licensePlate) parts.push(purchased.licensePlate);
  if (purchased.busLicensePlate) parts.push(purchased.busLicensePlate);
  if (purchased.departureCity && purchased.destinationCity) {
    parts.push(`${purchased.departureCity} → ${purchased.destinationCity}`);
  } else if (purchased.destinationCity || purchased.routeDestination) {
    parts.push(purchased.destinationCity || purchased.routeDestination);
  }
  const duration = formatCoverDuration(purchased.coverageDuration);
  if (duration) parts.push(duration);
  const amount = purchased.amount ?? purchased.coverageDuration?.price;
  if (amount != null && amount !== "") parts.push(`₵${amount}`);
  return parts.join(" · ");
};

const claimSearchText = (claim) => {
  const { incident, purchased, customer } = parseClaimParts(claim);
  const representative = claim.representative || {};
  return [
    claim.slug,
    claim.insurance_policy?.policy_name,
    claim.insurance_policy?.policy_type,
    incident.location,
    incident.type,
    incident.description,
    personName(customer),
    customer.email,
    customer.phone_number,
    representative.full_name,
    representative.relationship,
    representative.phone,
    representative.email,
    coverSummary(purchased),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const claimDocuments = (claim) =>
  (Array.isArray(claim?.documents) && claim.documents.length ? claim.documents : claim?.supporting_documents) || [];

const StatusBadge = ({ status }) => {
  const configs = {
    pending: {
      icon: Clock,
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      label: "Pending",
    },
    received: {
      icon: FileCheck,
      color: "bg-blue-100 text-blue-800 border-blue-200",
      label: "Received",
    },
    reviewed: {
      icon: AlertCircle,
      color: "bg-indigo-100 text-indigo-800 border-indigo-200",
      label: "Reviewed",
    },
    reviewing: {
      icon: AlertCircle,
      color: "bg-indigo-100 text-indigo-800 border-indigo-200",
      label: "Reviewing",
    },
    claimed: {
      icon: CheckCircle,
      color: "bg-green-100 text-green-800 border-green-200",
      label: "Claimed",
    },
    rejected: {
      icon: XCircle,
      color: "bg-red-100 text-red-800 border-red-200",
      label: "Rejected",
    },
  };

  const config = configs[status] || configs.pending;
  const Icon = config.icon;

  return (
    <motion.span
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}
    >
      <Icon className="mr-1 w-3 h-3" />
      {config.label}
    </motion.span>
  );
};

const MENU_WIDTH = 240;
const MENU_GAP = 8;
const VIEWPORT_PAD = 12;

const menuTone = {
  default: {
    row: "text-slate-700 hover:bg-slate-50",
    icon: "bg-slate-100 text-slate-500",
  },
  primary: {
    row: "text-blue-700 hover:bg-blue-50",
    icon: "bg-blue-100 text-blue-600",
  },
  success: {
    row: "text-emerald-700 hover:bg-emerald-50",
    icon: "bg-emerald-100 text-emerald-600",
  },
  danger: {
    row: "text-red-600 hover:bg-red-50",
    icon: "bg-red-100 text-red-600",
  },
  muted: {
    row: "text-slate-500 hover:bg-slate-50",
    icon: "bg-slate-100 text-slate-400",
  },
};

const buildClaimMenuItems = (status) => {
  const view = { id: "view", label: "View details", icon: Eye, tone: "default" };

  if (status === "pending") {
    return [view, { id: "receive", label: "Mark as received", icon: FileCheck, tone: "primary" }];
  }

  if (status === "received") {
    return [view, { id: "revertToPending", label: "Revert to pending", icon: ChevronLeft, tone: "muted" }];
  }

  if (status === "reviewed" || status === "reviewing") {
    return [
      view,
      { id: "claim", label: "Approve claim", icon: CheckCircle, tone: "success" },
      { id: "reject", label: "Reject claim", icon: XCircle, tone: "danger" },
      { divider: true },
      { id: "revertToReceived", label: "Revert to received", icon: ChevronLeft, tone: "muted" },
    ];
  }

  if (status === "claimed" || status === "rejected") {
    return [
      view,
      { divider: true },
      { id: "revertToReviewed", label: "Revert to reviewing", icon: ChevronLeft, tone: "muted" },
    ];
  }

  return [view];
};

const estimateMenuHeight = (items) =>
  12 + items.reduce((height, item) => height + (item.divider ? 9 : 40), 0);

const placeMenu = (button, menuHeight) => {
  const rect = button.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PAD;
  const spaceAbove = rect.top - VIEWPORT_PAD;
  const openUp = spaceBelow < menuHeight + MENU_GAP && spaceAbove > spaceBelow;
  const left = Math.max(
    VIEWPORT_PAD,
    Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - VIEWPORT_PAD)
  );
  const rawTop = openUp ? rect.top - MENU_GAP - menuHeight : rect.bottom + MENU_GAP;
  const top = Math.max(VIEWPORT_PAD, Math.min(rawTop, window.innerHeight - menuHeight - VIEWPORT_PAD));

  return { top, left, openUp, hidden: rect.bottom < 0 || rect.top > window.innerHeight };
};

const ClaimActionsMenu = ({ status, onSelect }) => {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const items = useMemo(() => buildClaimMenuItems(status), [status]);

  const syncPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return false;
    const height = menuRef.current?.offsetHeight || estimateMenuHeight(items);
    const next = placeMenu(button, height);
    if (next.hidden) return false;
    setCoords(next);
    return true;
  }, [items]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    if (!syncPosition()) setOpen(false);

    const onScroll = () => {
      if (!syncPosition()) setOpen(false);
    };

    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, syncPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggleMenu = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const button = buttonRef.current;
    if (!button) return;
    setCoords(placeMenu(button, estimateMenuHeight(items)));
    setOpen(true);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggleMenu}
        className={`inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border shadow-sm transition-colors ${
          open
            ? "border-blue-200 bg-blue-50 text-blue-700"
            : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
        }`}
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: "fixed", top: coords.top, left: coords.left, width: MENU_WIDTH, zIndex: 40 }}
            className="rounded-xl border border-slate-200/90 bg-white p-1.5 shadow-xl shadow-slate-900/10 ring-1 ring-slate-900/5"
          >
            {items.map((item, index) => {
              if (item.divider) {
                return <div key={`divider-${index}`} className="my-1 h-px bg-slate-100" />;
              }

              const Icon = item.icon;
              const tone = menuTone[item.tone] || menuTone.default;

              return (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onSelect(item.id);
                  }}
                  className={`flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm font-medium transition-colors ${tone.row}`}
                >
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${tone.icon}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  {item.label}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

const DateRangeField = forwardRef(function DateRangeField({ value, onClick, placeholder }, ref) {
  const hasValue = Boolean(value);

  return (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      className={`flex h-11 w-full items-center gap-2 rounded-xl border px-3 text-left text-sm transition ${
        hasValue
          ? "border-blue-200 bg-blue-50/70 text-slate-800"
          : "border-slate-200 bg-slate-50/80 text-slate-400 hover:border-slate-300 hover:bg-white"
      }`}
    >
      <Calendar className={`h-4 w-4 shrink-0 ${hasValue ? "text-blue-600" : "text-slate-400"}`} />
      <span className="truncate">{hasValue ? value : placeholder}</span>
    </button>
  );
});

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "in_review", label: "In review" },
  { value: "received", label: "Received" },
  { value: "reviewed", label: "Reviewed" },
  { value: "reviewing", label: "Reviewing" },
  { value: "claimed", label: "Claimed" },
  { value: "rejected", label: "Rejected" },
];

const STAT_TONES = {
  blue: {
    iconWrap: "bg-blue-50 text-blue-600",
    bar: "bg-blue-500",
    active: "border-blue-300 bg-blue-50/50 ring-4 ring-blue-100",
  },
  amber: {
    iconWrap: "bg-amber-50 text-amber-600",
    bar: "bg-amber-400",
    active: "border-amber-300 bg-amber-50/60 ring-4 ring-amber-100",
  },
  indigo: {
    iconWrap: "bg-indigo-50 text-indigo-600",
    bar: "bg-indigo-500",
    active: "border-indigo-300 bg-indigo-50/50 ring-4 ring-indigo-100",
  },
  emerald: {
    iconWrap: "bg-emerald-50 text-emerald-600",
    bar: "bg-emerald-500",
    active: "border-emerald-300 bg-emerald-50/50 ring-4 ring-emerald-100",
  },
  rose: {
    iconWrap: "bg-rose-50 text-rose-600",
    bar: "bg-rose-500",
    active: "border-rose-300 bg-rose-50/50 ring-4 ring-rose-100",
  },
};

const ClaimStatCard = ({ label, value, share, hint, icon: Icon, tone, active, onClick }) => {
  const colors = STAT_TONES[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`relative overflow-hidden rounded-2xl border bg-white p-4 text-left shadow-sm shadow-slate-200/60 transition duration-200 sm:p-5 ${
        active ? colors.active : "border-slate-200/80 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
      }`}
    >
      <span className={`absolute inset-y-3 left-0 w-1 rounded-r-full ${colors.bar}`} />
      <div className="flex items-start justify-between gap-3 pl-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
          <p className="mt-1 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
        </div>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${colors.iconWrap}`}>
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <div className="mt-4 pl-3">
        <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full ${colors.bar}`} style={{ width: `${share}%` }} />
        </div>
        <p className="mt-2 truncate text-xs font-medium text-slate-500">{hint}</p>
      </div>
    </button>
  );
};

const EmptyState = ({ icon: Icon, title, description }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="flex flex-col items-center justify-center py-16"
  >
    <div className="mb-4 p-4 bg-gray-100 rounded-full">
      <Icon className="w-12 h-12 text-gray-400" />
    </div>
    <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
    <p className="text-center text-gray-500 max-w-md">{description}</p>
  </motion.div>
);

const DrawerSection = ({ title, children }) => (
  <section className="px-6 py-5">
    <h3 className="mb-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{title}</h3>
    {children}
  </section>
);

const DrawerField = ({ label, children }) =>
  children ? (
    <div className="min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <div className="mt-1 text-sm leading-5 text-slate-900">{children}</div>
    </div>
  ) : null;

const ClaimDetailsDrawer = ({ isOpen, onClose, claim }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const policy = claim?.insurance_policy || {};
  const { incident, purchased, customer } = claim ? parseClaimParts(claim) : { incident: {}, purchased: {}, customer: {} };
  const boughtFor = claim ? policyBoughtFor(claim, customer) : null;
  const documents = claim ? claimDocuments(claim) : [];
  const buyerName = personName(customer);
  const duration = formatCoverDuration(purchased.coverageDuration);
  const amount = purchased.amount ?? purchased.coverageDuration?.price;
  const route =
    purchased.departureCity && purchased.destinationCity
      ? `${purchased.departureCity} → ${purchased.destinationCity}`
      : purchased.destinationCity || purchased.routeDestination;
  const representative = claim?.representative;

  return (
    <AnimatePresence>
      {isOpen && claim && (
        <>
          <motion.button
            type="button"
            aria-label="Close claim details"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-slate-900/40"
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="claim-drawer-title"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[480px] flex-col bg-white shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Claim {claim.slug?.slice(0, 8).toUpperCase()}
                </p>
                <h2 id="claim-drawer-title" className="mt-1 truncate text-lg font-semibold text-slate-900">
                  {policy.policy_name || "Claim details"}
                </h2>
                <p className="mt-1 text-sm capitalize text-slate-500">
                  {policy.policy_type?.replace(/_/g, " ") || "Insurance"}
                </p>
                <div className="mt-3">
                  <StatusBadge status={claim.claim_status} />
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 divide-y divide-slate-100 overflow-y-auto">
              <DrawerSection title="Incident">
                <div className="grid grid-cols-2 gap-4">
                  <DrawerField label="Date">
                    {incident.date ? format(parseISO(incident.date), "MMM dd, yyyy") : "—"}
                  </DrawerField>
                  <DrawerField label="Type">{incident.type || "—"}</DrawerField>
                  <DrawerField label="Location">{incident.location || "—"}</DrawerField>
                  <DrawerField label="Authorities">
                    <span className="capitalize">{incident.authorities || "—"}</span>
                  </DrawerField>
                </div>
                <div className="mt-4">
                  <DrawerField label="Description">{incident.description || "—"}</DrawerField>
                </div>
              </DrawerSection>

              <DrawerSection title="Bought for">
                <DrawerField label="Name">{boughtFor?.name}</DrawerField>
                <p className="mt-1 text-sm text-slate-500">{boughtFor?.meta}</p>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <DrawerField label="Phone">{boughtFor?.phone}</DrawerField>
                  <DrawerField label="Email">{boughtFor?.email}</DrawerField>
                </div>
                {!boughtFor?.forSelf && buyerName && (
                  <p className="mt-4 text-sm text-slate-500">
                    Purchased by <span className="font-medium text-slate-800">{buyerName}</span>
                  </p>
                )}
                {representative?.id_type && (
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <DrawerField label="ID type">{representative.id_type}</DrawerField>
                    <DrawerField label="ID number">{representative.id_number}</DrawerField>
                  </div>
                )}
              </DrawerSection>

              <DrawerSection title="Purchased cover">
                <div className="grid grid-cols-2 gap-4">
                  <DrawerField label="Vehicle">{purchased.vehicleName || purchased.vehicleType}</DrawerField>
                  <DrawerField label="Plate">{purchased.licensePlate || purchased.busLicensePlate}</DrawerField>
                  <DrawerField label="Route">{route}</DrawerField>
                  <DrawerField label="Duration">{duration}</DrawerField>
                  <DrawerField label="Amount">{amount != null && amount !== "" ? `₵${amount}` : null}</DrawerField>
                </div>
              </DrawerSection>

              {documents.length > 0 && (
                <DrawerSection title="Documents">
                  <ul className="space-y-2">
                    {documents.map((doc, index) => (
                      <li key={doc}>
                        <a
                          href={doc}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                        >
                          <span className="inline-flex min-w-0 items-center gap-2">
                            <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                            <span className="truncate">Document {index + 1}</span>
                          </span>
                          <span className="shrink-0 text-xs font-medium text-slate-500">Open</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </DrawerSection>
              )}

              {claim.declarations?.length > 0 && (
                <DrawerSection title="Declarations">
                  <ul className="space-y-3">
                    {claim.declarations.map((declaration) => (
                      <li key={declaration} className="text-sm leading-5 text-slate-700">
                        {declaration}
                      </li>
                    ))}
                  </ul>
                </DrawerSection>
              )}

              <DrawerSection title="Policy">
                <div className="grid grid-cols-2 gap-4">
                  <DrawerField label="Geography">
                    {policy.geography ? <span className="capitalize">{policy.geography}</span> : null}
                  </DrawerField>
                  <DrawerField label="Risk">
                    {policy.risk_level ? <span className="capitalize">{policy.risk_level}</span> : null}
                  </DrawerField>
                </div>
                {policy.description && (
                  <p className="mt-4 text-sm leading-5 text-slate-600">{policy.description}</p>
                )}
                {policy.insurance_coverage_list?.length > 0 && (
                  <ul className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
                    {policy.insurance_coverage_list.map((coverage) => (
                      <li key={coverage.title} className="flex items-start justify-between gap-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900">{coverage.title}</p>
                          {coverage.description && (
                            <p className="mt-0.5 text-xs leading-5 text-slate-500">{coverage.description}</p>
                          )}
                        </div>
                        {coverage.limit && (
                          <p className="shrink-0 text-sm font-medium text-slate-900">₵{coverage.limit}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {policy.eligibility_criteria?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Eligibility</p>
                    <ul className="mt-2 space-y-1.5">
                      {policy.eligibility_criteria.map((item) => (
                        <li key={item} className="text-sm text-slate-700">{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {policy.exclusions?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Exclusions</p>
                    <ul className="mt-2 space-y-1.5">
                      {policy.exclusions.map((item) => (
                        <li key={item} className="text-sm text-slate-700">{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {policy.terms && (
                  <div className="mt-4">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Terms</p>
                    {policy.terms.startsWith("http") ? (
                      <a href={policy.terms} target="_blank" rel="noopener noreferrer" className="mt-1 block text-sm text-slate-700 underline">
                        {policy.terms}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm leading-5 text-slate-600">{policy.terms}</p>
                    )}
                  </div>
                )}
              </DrawerSection>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};


export default function Claims() {
  const { isBlocked, AccountStatusModal } = useAccountStatusCheck();
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [currentPage, setCurrentPage] = useState(1);
  const [claimsData, setClaimsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: null, claim: null });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    const fetchClaims = async () => {
      setLoading(true);
      try {
        const response = await claimsService.getClaims();
        if (response.data?.status_code === 200 && response.data?.data) {
          setClaimsData(response.data.data || []);
        } else {
          toast.error(response.message || "Failed to fetch claims");
        }
      } catch (error) {
        toast.error("An error occurred while getting claims");
      } finally {
        setLoading(false);
      }
    };
    fetchClaims();
  }, []);

  // Calculate stats from real data
  const stats = useMemo(() => {
    const totalClaims = claimsData.length;
    const pendingClaims = claimsData.filter((c) => c.claim_status === "pending").length;
    const receivedClaims = claimsData.filter((c) => c.claim_status === "received").length;
    const reviewedClaims = claimsData.filter((c) => c.claim_status === "reviewed" || c.claim_status === "reviewing").length;
    const claimedClaims = claimsData.filter((c) => c.claim_status === "claimed").length;
    const rejectedClaims = claimsData.filter((c) => c.claim_status === "rejected").length;

    // Calculate average processing time (days between claim submission and current status)
    const processingTimes = claimsData
      .filter((c) => c.claim_status !== "pending")
      .map((c) => {
        // This would need created_at timestamp from API
        return 0; // Placeholder
      });
    const avgProcessingTime = processingTimes.length > 0
      ? Math.round(processingTimes.reduce((a, b) => a + b, 0) / processingTimes.length)
      : 0;

    return {
      totalClaims,
      pendingClaims,
      receivedClaims,
      reviewedClaims,
      claimedClaims,
      rejectedClaims,
      averageProcessingTime: avgProcessingTime,
    };
  }, [claimsData]);


  const handleStatusChange = async (claim, newStatus, rejectReason = null) => {
    setUpdatingStatus(true);
    try {
      const response = await claimsService.updateClaimStatus(claim.slug, newStatus, rejectReason, claim.slug);
      if (response.data.status_code===200) {
        toast.success(response.data.message || "Claim status updated successfully");
        // Refresh claims
        const refreshResponse = await claimsService.getClaims();
        if (refreshResponse.status && refreshResponse.data) {
          // Handle different response structures
          const data = refreshResponse.data;
          if (Array.isArray(data)) {
            setClaimsData(data);
          } else if (data?.data && Array.isArray(data.data)) {
            setClaimsData(data.data);
          } else if (data?.data?.data && Array.isArray(data.data.data)) {
            setClaimsData(data.data.data);
          }
        }
        setConfirmModal({ isOpen: false, action: null, claim: null });
      } else {
        toast.error(response.data.message || "Failed to update claim status");
      }
    } catch (error) {
      toast.error("An error occurred while updating claim status");
    } finally {
      setUpdatingStatus(false);
    }
  };


  const openConfirmModal = (claim, action) => {
    setConfirmModal({ isOpen: true, action, claim });
  };

  const getActionConfig = (action) => {
    const configs = {
      receive: {
        title: "Mark as Received",
        message: "Are you sure you want to mark this claim as received? This will move it to the next stage of review.",
        confirmText: "Mark as Received",
        variant: "info",
      },
      review: {
        title: "Mark as Reviewed",
        message: "Are you sure you want to mark this claim as reviewed? You will then be able to approve or reject it.",
        confirmText: "Mark as Reviewed",
        variant: "info",
      },
      claim: {
        title: "Approve Claim",
        message: "Are you sure you want to approve this claim? This action cannot be undone.",
        confirmText: "Approve Claim",
        variant: "warning",
      },
      reject: {
        title: "Reject Claim",
        message: "Are you sure you want to reject this claim? Please provide a reason for rejection.",
        confirmText: "Reject Claim",
        variant: "danger",
      },
      // Backward actions
      revertToPending: {
        title: "Revert to Pending",
        message: "Are you sure you want to move this claim back to pending status? This will undo the received status.",
        confirmText: "Revert to Pending",
        variant: "warning",
      },
      revertToReceived: {
        title: "Revert to Received",
        message: "Are you sure you want to move this claim back to received status? This will undo the reviewed status.",
        confirmText: "Revert to Received",
        variant: "warning",
      },
      revertToReviewed: {
        title: "Revert to Reviewing",
        message: "Are you sure you want to move this claim back to reviewing status? This will undo the approval/rejection.",
        confirmText: "Revert to Reviewing",
        variant: "warning",
      },
    };
    return configs[action] || configs.receive;
  };

  const filteredClaims = useMemo(() => {
    return claimsData.filter((claim) => {
      const matchesGlobal =
        globalFilter === "" || claimSearchText(claim).includes(globalFilter.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        claim.claim_status === statusFilter ||
        (statusFilter === "in_review" && ["received", "reviewed", "reviewing"].includes(claim.claim_status)) ||
        (statusFilter === "reviewed" && claim.claim_status === "reviewing") ||
        (statusFilter === "reviewing" && claim.claim_status === "reviewed");

      const incidentDate = parseClaimParts(claim).incident?.date;
      const matchesDateRange =
        !startDate ||
        !endDate ||
        (incidentDate &&
          parseISO(incidentDate) >= startDate &&
          parseISO(incidentDate) <= endDate);

      return matchesGlobal && matchesStatus && matchesDateRange;
    });
  }, [claimsData, globalFilter, statusFilter, startDate, endDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [globalFilter, statusFilter, startDate, endDate]);

  const hasActiveFilters = Boolean(globalFilter || statusFilter !== "all" || startDate || endDate);
  const statusLabel = STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label;

  const clearFilters = () => {
    setGlobalFilter("");
    setStatusFilter("all");
    setDateRange([null, null]);
  };

  const totalPages = Math.ceil(filteredClaims.length / pageSize);
  const paginatedClaims = filteredClaims.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (loading) {
    return <Loader fullScreen={true} text="Loading claims..." />;
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      {/* Account Status Modal */}
      <AccountStatusModal />

      <div className={`mx-auto max-w-full ${isBlocked ? 'pointer-events-none opacity-30 blur-sm transition-all duration-300' : ''}`}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <h1 className="mb-2 text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Claims Management
              </h1>
              <p className="text-gray-600">
                Review and manage insurance claims with comprehensive analytics
              </p>
            </div>

            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center px-4 py-2 space-x-2 bg-white rounded-lg border border-gray-200 transition-all duration-200 hover:shadow-md"
              >
                <Download className="w-4 h-4" />
                <span>Export Report</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5"
        >
          {[
            {
              id: "all",
              label: "Total claims",
              value: stats.totalClaims,
              icon: FileText,
              tone: "blue",
            },
            {
              id: "pending",
              label: "Pending",
              value: stats.pendingClaims,
              icon: Clock,
              tone: "amber",
            },
            {
              id: "in_review",
              label: "In review",
              value: stats.receivedClaims + stats.reviewedClaims,
              icon: FileCheck,
              tone: "indigo",
            },
            {
              id: "claimed",
              label: "Approved",
              value: stats.claimedClaims,
              icon: CheckCircle,
              tone: "emerald",
            },
            {
              id: "rejected",
              label: "Rejected",
              value: stats.rejectedClaims,
              icon: XCircle,
              tone: "rose",
            },
          ].map((card) => {
            const share = stats.totalClaims ? Math.round((card.value / stats.totalClaims) * 100) : 0;
            const hint =
              card.id === "all"
                ? stats.totalClaims
                  ? "All claims in the queue"
                  : "No claims yet"
                : card.value
                  ? `${share}% of all claims`
                  : "None in this stage";

            return (
              <ClaimStatCard
                key={card.id}
                label={card.label}
                value={card.value}
                share={card.id === "all" ? (stats.totalClaims ? 100 : 0) : share}
                hint={hint}
                icon={card.icon}
                tone={card.tone}
                active={statusFilter === card.id}
                onClick={() => setStatusFilter(card.id)}
              />
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-4 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm shadow-slate-200/60"
        >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/80 px-3 transition focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-500/10">
              <Search className="h-4 w-4 shrink-0 text-slate-400" />
              <input
                type="text"
                placeholder="Search claimant, policy, cover, or location"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="h-full w-full border-0 bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
              />
              {globalFilter && (
                <button
                  type="button"
                  onClick={() => setGlobalFilter("")}
                  className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="relative w-full lg:w-48">
              <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
                className={`h-11 w-full appearance-none rounded-xl border py-0 pl-9 pr-9 text-sm font-medium outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 ${
                  statusFilter !== "all"
                    ? "border-blue-200 bg-blue-50/70 text-slate-800"
                    : "border-slate-200 bg-slate-50/80 text-slate-700 hover:border-slate-300"
                }`}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>

            <div className="w-full lg:w-64">
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                placeholderText="Incident date range"
                customInput={<DateRangeField />}
                wrapperClassName="block w-full"
                popperClassName="!z-50"
                popperContainer={({ children }) => createPortal(children, document.body)}
                dateFormat="MMM d, yyyy"
                isClearable={false}
              />
            </div>

            <p className="shrink-0 text-sm text-slate-500 lg:pl-1">
              <span className="font-semibold text-slate-800">{filteredClaims.length}</span>{" "}
              {filteredClaims.length === 1 ? "claim" : "claims"}
            </p>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
              {globalFilter && (
                <button
                  type="button"
                  onClick={() => setGlobalFilter("")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-200"
                >
                  Search: {globalFilter}
                  <X className="h-3 w-3" />
                </button>
              )}
              {statusFilter !== "all" && (
                <button
                  type="button"
                  onClick={() => setStatusFilter("all")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
                >
                  {statusLabel}
                  <X className="h-3 w-3" />
                </button>
              )}
              {(startDate || endDate) && (
                <button
                  type="button"
                  onClick={() => setDateRange([null, null])}
                  className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 transition hover:bg-indigo-100"
                >
                  {startDate ? format(startDate, "MMM d, yyyy") : "Start"}
                  {" – "}
                  {endDate ? format(endDate, "MMM d, yyyy") : "End"}
                  <X className="h-3 w-3" />
                </button>
              )}
              <button
                type="button"
                onClick={clearFilters}
                className="ml-auto text-xs font-semibold text-slate-500 transition hover:text-slate-800"
              >
                Clear all
              </button>
            </div>
          )}
        </motion.div>

        {/* Claims Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm"
        >
          {filteredClaims.length === 0 ? (
            <div className="py-20">
              <EmptyState
                icon={FileText}
                title="No Claims Found"
                description="No claims match your current filters. Try adjusting your search criteria."
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1180px] table-fixed">
                  <colgroup>
                    <col className="w-[132px]" />
                    <col className="w-[16%]" />
                    <col className="w-[18%]" />
                    <col />
                    <col className="w-[148px]" />
                    <col className="w-[15%]" />
                    <col className="w-[108px]" />
                    <col className="w-[96px]" />
                  </colgroup>
                  <thead className="border-b border-slate-200 bg-slate-50/80">
                    <tr>
                      {["Claim ID", "Bought for", "Policy", "Cover", "Incident Date", "Location", "Status", "Actions"].map((label) => (
                        <th
                          key={label}
                          className="whitespace-nowrap px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500"
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    <AnimatePresence>
                      {paginatedClaims.map((claim, index) => {
                        const { incident, purchased, customer } = parseClaimParts(claim);
                        const policy = claim.insurance_policy || {};
                        const currentStatus = claim.claim_status || "pending";
                        const policyName = policy.policy_name || "N/A";
                        const policyType = policy.policy_type?.replace(/_/g, " ") || "N/A";
                        const location = incident.location || "N/A";
                        const boughtFor = policyBoughtFor(claim, customer);
                        const cover = coverSummary(purchased) || "N/A";

                        return (
                          <motion.tr
                            key={claim.slug}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="transition-colors hover:bg-slate-50/80"
                          >
                            <td className="whitespace-nowrap px-5 py-3.5 align-middle">
                              <span className="inline-flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50/80 px-2.5 py-1.5">
                                <FileText className="h-3.5 w-3.5 text-blue-600" />
                                <span className="font-mono text-xs font-semibold text-blue-700">
                                  {claim.slug?.slice(0, 8).toUpperCase()}
                                </span>
                              </span>
                            </td>
                            <td className="max-w-0 px-5 py-3.5 align-middle">
                              <div className="flex min-w-0 items-center gap-2" title={`${boughtFor.name} · ${boughtFor.meta}`}>
                                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${boughtFor.forSelf ? "bg-blue-50 text-blue-600" : "bg-orange-50 text-orange-600"}`}>
                                  <User className="h-3.5 w-3.5" />
                                </span>
                                <div className="min-w-0">
                                  <p className="truncate whitespace-nowrap text-sm font-semibold text-slate-900">{boughtFor.name}</p>
                                  <p className="truncate whitespace-nowrap text-xs text-slate-500">{boughtFor.meta}</p>
                                </div>
                              </div>
                            </td>
                            <td className="max-w-0 px-5 py-3.5 align-middle">
                              <p
                                className="truncate whitespace-nowrap text-sm"
                                title={`${policyName} · ${policyType}`}
                              >
                                <span className="font-semibold text-slate-900">{policyName}</span>
                                <span className="mx-1.5 text-slate-300">·</span>
                                <span className="text-xs capitalize text-slate-500">{policyType}</span>
                              </p>
                            </td>
                            <td className="max-w-0 px-5 py-3.5 align-middle">
                              <p className="truncate whitespace-nowrap text-sm text-slate-700" title={cover}>
                                {cover}
                              </p>
                            </td>
                            <td className="whitespace-nowrap px-5 py-3.5 align-middle text-sm text-slate-700">
                              {incident.date
                                ? format(parseISO(incident.date), "MMM dd, yyyy")
                                : "N/A"}
                            </td>
                            <td className="max-w-0 px-5 py-3.5 align-middle">
                              <div className="flex min-w-0 items-center gap-1.5 text-sm text-slate-700" title={location}>
                                <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
                                <span className="min-w-0 truncate whitespace-nowrap">{location}</span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-5 py-3.5 align-middle">
                              <StatusBadge status={currentStatus} />
                            </td>
                            <td className="whitespace-nowrap px-5 py-3.5 align-middle">
                              <ClaimActionsMenu
                                status={currentStatus}
                                onSelect={(actionId) => {
                                  if (actionId === "view") {
                                    setSelectedClaim(claim);
                                    setShowDetailsModal(true);
                                    return;
                                  }
                                  openConfirmModal(claim, actionId);
                                }}
                              />
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex flex-col items-center justify-between lg:flex-row">
                    <div className="flex items-center mb-4 space-x-2 lg:mb-0">
                      <span className="text-sm text-gray-700">
                        Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredClaims.length)} of {filteredClaims.length} claims
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>

                      <span className="px-4 py-2 text-sm text-gray-700">
                        Page {currentPage} of {totalPages || 1}
                      </span>

                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage >= totalPages}
                        className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* Claim details */}
      <ClaimDetailsDrawer
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedClaim(null);
        }}
        claim={selectedClaim}
      />

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen && confirmModal.action !== "reject" && !confirmModal.action?.startsWith("revert")}
        onClose={() => setConfirmModal({ isOpen: false, action: null, claim: null })}
        onConfirm={() => {
          const { claim, action } = confirmModal;
          if (action === "receive") {
            handleStatusChange(claim, "received");
          } else if (action === "review") {
            handleStatusChange(claim, "reviewing");
          } else if (action === "claim") {
            handleStatusChange(claim, "claimed");
          }
        }}
        title={getActionConfig(confirmModal.action)?.title}
        message={getActionConfig(confirmModal.action)?.message}
        confirmText={getActionConfig(confirmModal.action)?.confirmText}
        variant={getActionConfig(confirmModal.action)?.variant}
        isLoading={updatingStatus}
      />

      {/* Backward Movement Confirmation Modals */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen && confirmModal.action?.startsWith("revert")}
        onClose={() => setConfirmModal({ isOpen: false, action: null, claim: null })}
        onConfirm={() => {
          const { claim, action } = confirmModal;
          if (action === "revertToPending") {
            handleStatusChange(claim, "pending");
          } else if (action === "revertToReceived") {
            handleStatusChange(claim, "received");
          } else if (action === "revertToReviewed") {
            handleStatusChange(claim, "reviewing");
          }
        }}
        title={getActionConfig(confirmModal.action)?.title}
        message={getActionConfig(confirmModal.action)?.message}
        confirmText={getActionConfig(confirmModal.action)?.confirmText}
        variant={getActionConfig(confirmModal.action)?.variant}
        isLoading={updatingStatus}
      />

      {/* Reject Confirmation Modal with reason input */}
      <RejectClaimModal
        isOpen={confirmModal.isOpen && confirmModal.action === "reject"}
        onClose={() => setConfirmModal({ isOpen: false, action: null, claim: null })}
        onConfirm={(reason) => {
          handleStatusChange(confirmModal.claim, "rejected", reason);
        }}
        isLoading={updatingStatus}
      />
    </div>
  );
}

// Reject Claim Modal with reason input
const RejectClaimModal = ({ isOpen, onClose, onConfirm, isLoading }) => {
  const [rejectReason, setRejectReason] = useState("");

  const handleConfirm = () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    onConfirm(rejectReason);
    setRejectReason("");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Reject Claim</h3>
                  <button
                    onClick={onClose}
                    disabled={isLoading}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="mr-2 w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">
                      Are you sure you want to reject this claim? Please provide a reason for rejection.
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter the reason for rejecting this claim..."
                    rows={4}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    disabled={isLoading}
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={onClose}
                    disabled={isLoading}
                    className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-xl font-medium transition-colors hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={isLoading || !rejectReason.trim()}
                    className="flex-1 px-4 py-3 text-white bg-red-600 rounded-xl font-medium transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        Rejecting...
                      </div>
                    ) : (
                      "Reject Claim"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
